import DialogModalContent from '@/features/shared/DialogModalContent';
import { MuiContainer, MuiIconButton } from '@/features/shared/material-ui';
import MessageBar from '@/features/shared/MessageBar';
import { ColorNames, Theme } from '@/utils/constants';
import { Close as CloseIcon } from '@material-ui/icons';
import clsx from 'clsx';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import MarketoForm from './MarketoForm';
import { MarketoFormModalProps } from './MarketoForm.interface';
import useStyles from './MarketoForm.styles';
import { useMarketoFormModalDatalayer } from '@/features/MarketoForm/MarketoFormModal.datalayer';

const MarketoFormModal = ({
  heading,
  formDescription,
  baseUrl,
  munchkinId,
  confirmationMessage,
  formId,
  onSuccess = () => {},
  contentButtons,
  theme = Theme.light,
  isOpen,
  onClose = () => {},
  rules,
  recaptchaSitekey,
  recaptchaLegal,
  containerColor = ColorNames.white,
  datalayerVariant = '',
  datalayerPosition = '',
  emailBlacklistErrorMessage,
  emailFieldname,
  emailsBlacklisted,
  emailCRMFieldname,
  CRMSyncFieldnames,
  isOpenedByCTAVariant,
  datalayerLinkText = '',
  labels = {},
}: Br.PropsWithContentButtons<MarketoFormModalProps>) => {
  const classes = useStyles({});

  const [messageBarOpen, setMessageBarOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { dataLayerExitPopupForm, setDataLayerFormAttributes } = useMarketoFormModalDatalayer(
    formId,
    containerRef,
  );

  const handleCloseMessageBar = useCallback(() => {
    onClose();
    setMessageBarOpen(false);
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    onSuccess();
    setMessageBarOpen(true);
  }, [onSuccess]);

  const formProps = useMemo(
    () => ({
      baseUrl,
      munchkinId,
      confirmationMessage,
      formId,
      onSuccess: handleSubmit,
      contentButtons,
      theme,
      displayMessage: false,
      rules,
      recaptchaSitekey,
      recaptchaLegal,
      heading,
      formDescription,
      containerColor,
      emailBlacklistErrorMessage,
      emailFieldname,
      emailsBlacklisted,
      emailCRMFieldname,
      CRMSyncFieldnames,
      isOpenedByCTAVariant,
      datalayerLinkText,
      setParentDataLayerFormAttributes: setDataLayerFormAttributes,
      labels,
    }),
    [
      baseUrl,
      munchkinId,
      confirmationMessage,
      formId,
      handleSubmit,
      contentButtons,
      theme,
      rules,
      recaptchaSitekey,
      recaptchaLegal,
      heading,
      formDescription,
      containerColor,
      emailBlacklistErrorMessage,
      emailFieldname,
      emailsBlacklisted,
      emailCRMFieldname,
      CRMSyncFieldnames,
      isOpenedByCTAVariant,
      datalayerLinkText,
      labels,
      setDataLayerFormAttributes,
    ],
  );

  const messageBarProps = useMemo(
    () => ({
      isOpen: messageBarOpen,
      onClose: handleCloseMessageBar,
      message: confirmationMessage,
      autoHideDuration: null,
      hasOverlay: true,
    }),
    [confirmationMessage, handleCloseMessageBar, messageBarOpen],
  );

  const onCloseButton = React.useCallback(() => {
    dataLayerExitPopupForm();
    onClose();
  }, [dataLayerExitPopupForm, onClose]);

  return (
    <>
      <DialogModalContent isOpen={isOpen} keepMounted={false}>
        <MuiContainer disableGutters className={clsx('small', classes.modalContainer)}>
          <div
            className={classes.modalBackground}
            data-datalayer-variant={datalayerVariant}
            data-datalayer-position={datalayerPosition}
            ref={containerRef}
          >
            <MuiIconButton onClick={onCloseButton} className={classes.modalClose}>
              <CloseIcon />
            </MuiIconButton>
            <MarketoForm {...formProps} isModalForm={true} />
          </div>
        </MuiContainer>
      </DialogModalContent>
      {confirmationMessage && (
        <MessageBar {...messageBarProps} message={confirmationMessage} severity="success" />
      )}
    </>
  );
};

export default memo(MarketoFormModal);
